/**
 * SOMEBODY RANG. START A JOB.
 *
 * Anthony: "I can't seem to add a customer, job or quote anywhere easily
 * if it didn't come in through a channel automated already."
 *
 * He is right, and it is the plainest gap in the product. Every job in
 * here arrived through a webhook, a lead or a bridge; there was no way for
 * a person at the counter to say "this man is standing in front of me,
 * make him a job". The whole system was built for work that announces
 * itself, and half of a shop's work does not.
 *
 * ONE SHEET, THREE FIELDS. A name, a number, and what they want. That is
 * what an advisor has when the phone is still at their ear, and asking for
 * a VIN before they can write anything down is how the note ends up on
 * paper instead.
 *
 * IT MATCHES BEFORE IT CREATES. The same number is the same person, so a
 * regular who rings for the fourth time gets a fourth job on the record
 * they already have rather than a fourth copy of themselves. That is the
 * duplicate problem being prevented rather than cleaned up later.
 *
 * The car is optional here on purpose — the job card has a proper add-the-
 * car form with a VIN decoder, and that is the right place for it. This
 * one only has to get the thing written down before the call ends.
 */

import { db } from "../data/client.ts";
import { listCustomers, type CustomerRow } from "../data/customers.ts";
import { esc, firstSentence } from "../ui/dom.ts";
import { shopWords } from "../ui/words.ts";
import { cap } from "../domain/trade.ts";
import { lastKnownLocation } from "../data/jobs.ts";

const ten = (s: string) => s.replace(/\D/g, "").slice(-10);

/** A car, as it is written on a customer's record. */
type PickedCar = {
  id: string; year: number | null; make: string | null; model: string | null;
};

/** "2013 Audi A6", or whatever of it there is. */
function carWords(v: PickedCar): string {
  const s = [v.year ?? "", v.make ?? "", v.model ?? ""].join(" ").replace(/\s+/g, " ").trim();
  return s || `A ${shopWords().thing} with no details on it`;
}

/**
 * WHAT IT IS CALLED IS THE WHOLE PROBLEM.
 *
 * Anthony: "we want to send Jarrod Smith a new quote on trans swap. But,
 * we can't find where to add a new quote. Also, there is no option we can
 * find to add a new lead, customer, job, quote etc."
 *
 * The button existed. On the Jobs page, the Quotes page and the Customers
 * page, all three said **"Start a job"** — and a person looking to write a
 * quote does not read that as the thing they want, so as far as the shop
 * was concerned there was no way to do it. A feature nobody can find is a
 * feature nobody has.
 *
 * The four things are one thing underneath: a customer, and usually a job
 * on them at some status. So this is one sheet that knows which word the
 * person came looking for, and says that word back to them.
 *
 *   lead      somebody who got in touch. No job yet.
 *   quote     a job at estimate, which is what a quote IS here.
 *   job       the same, and the caller decides where it starts.
 *   customer  just the person, for somebody being put on file.
 */
export type NewKind = "lead" | "quote" | "job" | "customer";

const WORDS: Record<NewKind, {
  title: string; blurb: string; go: string; want: string; hint: string;
}> = {
  lead: {
    title: "New lead",
    blurb: "Somebody got in touch and there is nothing priced yet.",
    go: "Add the lead",
    want: "What did they ask for?",
    hint: "Rang about a transmission slipping",
  },
  quote: {
    title: "New quote",
    blurb: "A price for work somebody is thinking about. Nothing is booked.",
    go: "Start the quote",
    want: "What are we quoting?",
    hint: "Transmission swap on a 2004 F-150",
  },
  job: {
    title: "Start a job",
    blurb: "For work that did not come in through a form or a call.",
    go: "Start it",
    want: "What do they want?",
    hint: "Brakes grinding at the front, started last week",
  },
  customer: {
    title: "Add a customer",
    blurb: "Put somebody on file. You can price work for them afterwards.",
    go: "Add them",
    want: "Anything worth noting",
    hint: "Regular. Prefers texts, works nights.",
  },
};

/** Open the sheet. `then` gets the new job's id, or the customer's. */
export function newJobSheet(
  then: (id: string) => void, kind: NewKind = "job",
): void {
  const w = WORDS[kind];
  document.getElementById("njsheet")?.remove();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back" id="njsheet">
      <div class="sheet" role="dialog" aria-modal="true">
        <div class="sheet-head"><div><h2>${w.title}</h2>
          <div class="quiet">${w.blurb}</div></div>
          <button class="sheet-x" id="nj-x" aria-label="Close">&times;</button></div>
        <div class="sheet-body">
          <label class="field"><span>Already on file?
            <i>&mdash; search a name or a number</i></span>
            <input id="nj-find" autocomplete="off"
              placeholder="Start typing and pick them out"></label>
          <div id="nj-hits"></div>
          <div id="nj-using"></div>
          <label class="field"><span>Their name</span>
            <input id="nj-name" autocomplete="name"></label>
          <label class="field"><span>Mobile</span>
            <input id="nj-phone" type="tel" inputmode="tel" autocomplete="tel"></label>
          <p class="quiet small" id="nj-match"></p>
          <label class="field"><span>${w.want}</span>
            <textarea id="nj-want" rows="3"></textarea></label>
          <div id="nj-carslot">
            <label class="field"><span>The ${esc(shopWords().thing)} <i>&mdash; optional</i></span>
              <input id="nj-car"></label>
          </div>
          <p class="quiet small">The ${esc(shopWords().serial)} and the rest go on the job
             card. Get it written down first.</p>
        </div>
        <div class="sheet-acts">
          <button class="btn primary" id="nj-go">${w.go}</button>
          <button class="btn ghost" id="nj-cancel">Cancel</button>
        </div>
        <div class="msg-slot" id="nj-msg"></div>
      </div>
    </div>`);

  const shut = () => document.getElementById("njsheet")?.remove();
  document.getElementById("nj-x")?.addEventListener("click", shut);
  document.getElementById("nj-cancel")?.addEventListener("click", shut);

  const val = (id: string) =>
    (document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null)
      ?.value.trim() ?? "";

  /**
   * ─── FIND THEM INSTEAD OF TYPING THEM AGAIN ──────────────────────────
   *
   * Anthony: "the system has no lookup capability to recall previous
   * customer. This means we have to recreate a new customer."
   *
   * The matching underneath was already right — the same number is the
   * same person, and the shop has no duplicate customers because of it.
   * But that match only happens at the moment the button is pressed, and
   * only on a ten digit mobile. So from the counter it still looked like
   * there was no lookup: a regular's name, number and car all had to be
   * typed out again from memory, and if the advisor did not have the
   * number in front of them there was no way to reach the record at all.
   *
   * The matching stays as the backstop. This is the part a person can
   * see: search by name or number, pick them, and their record comes
   * with them — including the cars already on it, so a fourth visit
   * stops adding a fourth copy of the same Audi.
   */
  let picked: CustomerRow | null = null;
  let pickedCars: PickedCar[] = [];

  const setVal = (id: string, v: string) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) el.value = v;
  };

  /** The car field becomes a list of theirs, with room for a new one. */
  const paintCars = (): void => {
    const slot = document.getElementById("nj-carslot");
    if (!slot) return;
    if (!picked || !pickedCars.length) {
      slot.innerHTML = `<label class="field"><span>The ${esc(shopWords().thing)} <i>&mdash; optional</i></span>
          <input id="nj-car"></label>`;
      return;
    }
    /* NOTHING IS CHOSEN FOR THEM. Defaulting to the newest car would put a
       job on a vehicle nobody named, which on a two car household is a
       misfiled job that reads as a real one. The car stays optional here,
       exactly as it was before there was a list. */
    slot.innerHTML = `<label class="field"><span>The ${esc(shopWords().thing)}
          <i>&mdash; ${pickedCars.length === 1 ? "one" : pickedCars.length} on their record</i></span>
        <select id="nj-carpick">
          <option value="">Not saying yet</option>
          ${pickedCars.map((v) =>
            `<option value="${esc(v.id)}">${esc(carWords(v))}</option>`).join("")}
          <option value="new">Another ${esc(shopWords().thing)}, not listed here</option>
        </select></label>
      <div id="nj-newcar"></div>`;
    const pick = document.getElementById("nj-carpick") as HTMLSelectElement | null;
    const extra = document.getElementById("nj-newcar");
    /* Only asked for when "another car" is chosen. An empty box under a
       car they already picked is an invitation to make the duplicate. */
    const showExtra = () => {
      if (!extra) return;
      extra.innerHTML = pick && pick.value === "new"
        ? `<label class="field"><span>The other ${esc(shopWords().thing)}</span><input id="nj-car"></label>`
        : "";
    };
    pick?.addEventListener("change", showExtra);
    showExtra();
  };

  const useNobody = (): void => {
    picked = null;
    pickedCars = [];
    const using = document.getElementById("nj-using");
    if (using) using.innerHTML = "";
    paintCars();
  };

  const usePerson = async (row: CustomerRow): Promise<void> => {
    picked = row;
    setVal("nj-name", row.full_name);
    setVal("nj-phone", row.phone ?? "");
    setVal("nj-find", "");
    const hits = document.getElementById("nj-hits");
    if (hits) hits.innerHTML = "";
    const note = document.getElementById("nj-match");
    if (note) note.textContent = "";
    const using = document.getElementById("nj-using");
    if (using) {
      using.innerHTML = `<p class="msg ok">This goes on <b>${esc(row.full_name)}</b>'s
        record. <button class="linkbtn" id="nj-notthem">Not them</button></p>`;
      document.getElementById("nj-notthem")?.addEventListener("click", () => {
        setVal("nj-name", ""); setVal("nj-phone", "");
        useNobody();
        (document.getElementById("nj-find") as HTMLInputElement | null)?.focus();
      });
    }
    // Their cars, so the job can be put on one instead of beside it.
    try {
      const sb = await db();
      const { data } = await sb.from("vehicles")
        .select("id,year,make,model").eq("customer_id", row.id)
        .is("archived_at", null).order("created_at", { ascending: false });
      if (picked?.id !== row.id) return;      // they picked somebody else meanwhile
      pickedCars = (data ?? []) as PickedCar[];
    } catch { pickedCars = []; }              // no cars listed is not an error
    paintCars();
  };

  const findBox = document.getElementById("nj-find") as HTMLInputElement | null;
  let findTimer = 0;
  let findSeq = 0;
  findBox?.addEventListener("input", () => {
    window.clearTimeout(findTimer);
    const term = findBox.value.trim();
    const hits = document.getElementById("nj-hits");
    if (!hits) return;
    if (term.length < 2) { hits.innerHTML = ""; return; }
    findTimer = window.setTimeout(async () => {
      const mine = ++findSeq;
      let rows: CustomerRow[] = [];
      try { rows = (await listCustomers({ search: term, limit: 6 })).rows; }
      catch { if (mine === findSeq) hits.innerHTML =
        `<p class="quiet small">Could not search just now. Type them in below.</p>`;
        return; }
      if (mine !== findSeq) return;           // an older search coming back late
      const host = document.getElementById("nj-hits");
      if (!host) return;
      host.innerHTML = rows.length
        ? `<div class="njhits">${rows.map((r) =>
            `<button type="button" class="njhit" data-id="${esc(r.id)}">
               <b>${esc(r.full_name)}</b>
               <span>${esc(r.phone ?? "no number on file")}</span></button>`).join("")}</div>`
        : `<p class="quiet small">Nobody on file matches that. Fill it in below
             and they get added.</p>`;
      host.querySelectorAll<HTMLButtonElement>(".njhit").forEach((b) =>
        b.addEventListener("click", () => {
          const r = rows.find((x) => x.id === b.dataset["id"]);
          if (r) void usePerson(r);
        }));
    }, 250);
  });

  /**
   * Say who this is BEFORE the button is pressed. An advisor typing a
   * number they have rung before should see the name come back rather
   * than find out afterwards that they made a second copy of somebody.
   */
  const phoneBox = document.getElementById("nj-phone");
  phoneBox?.addEventListener("blur", async () => {
    const note = document.getElementById("nj-match");
    const p = ten(val("nj-phone"));
    if (!note) return;
    // Already said out loud, up in the picker. Saying it twice reads as
    // two different people.
    if (picked) { note.textContent = ""; return; }
    if (p.length !== 10) { note.textContent = ""; return; }
    const sb = await db();
    const { data } = await sb.from("customers")
      .select("full_name").eq("phone10", p).limit(1).maybeSingle();
    const who = (data as { full_name: string } | null)?.full_name;
    note.innerHTML = who
      ? `On file already as <b>${esc(who)}</b> &mdash; this goes on their record.`
      : "New to the shop.";
  });

  document.getElementById("nj-go")?.addEventListener("click", async () => {
    const b = document.getElementById("nj-go") as HTMLButtonElement;
    const m = document.getElementById("nj-msg");
    const say = (t: string) => { if (m) m.innerHTML = `<p class="msg bad">${esc(t)}</p>`; };

    const name = val("nj-name");
    const phone = ten(val("nj-phone"));
    const want = val("nj-want");
    if (!name) return say("It needs a name to put on it.");
    if (phone.length !== 10) return say("A ten digit mobile, so somebody can ring them back.");

    b.disabled = true; b.textContent = "Saving…";
    try {
      const sb = await db();
      const { data: me } = await sb.auth.getUser();
      const { data: prof } = await sb.from("profiles")
        .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
      const accountId = (prof as { account_id: string } | null)?.account_id;
      if (!accountId) throw new Error("Cannot tell which shop you belong to. Sign in again.");

      /* Picked off the list is the strongest answer there is — it is the
         record somebody looked at and said yes to. Only when nobody was
         picked does the number get asked, which is the old behavior and
         still the backstop for an advisor who types rather than searches. */
      let customerId: string | null = picked?.id ?? null;
      if (!customerId) {
        const { data: hit } = await sb.from("customers")
          .select("id").eq("phone10", phone).limit(1).maybeSingle();
        customerId = (hit as { id: string } | null)?.id ?? null;
      }

      /* Picked somebody whose record has no number, and typed one in: keep
         it, or they are just as unreachable by number next time. Never
         fatal — a viewer cannot write, and that must not lose the job. */
      if (picked && !ten(picked.phone ?? "").length && phone.length === 10) {
        try { await sb.from("customers").update({ phone: val("nj-phone") })
          .eq("id", picked.id); } catch { /* the job matters more */ }
      }

      if (!customerId) {
        const token = Array.from(crypto.getRandomValues(new Uint8Array(24)),
          (x) => x.toString(16).padStart(2, "0")).join("");
        const { data: made, error } = await sb.from("customers").insert({
          account_id: accountId, full_name: name, phone: val("nj-phone"),
          source: "counter", portal_token: token,
        }).select("id").single();
        if (error) throw new Error(`Could not save the customer: ${error.message}`);
        customerId = (made as { id: string }).id;
      }

      // The car, only when they gave one. A blank vehicle row helps nobody.
      let vehicleId: string | null = null;
      /* One of theirs, chosen from the list. This is the line that stops
         the fourth visit filing a fourth copy of the same car. */
      const carPick = (document.getElementById("nj-carpick") as HTMLSelectElement | null)?.value;
      if (carPick && carPick !== "new") vehicleId = carPick;
      // Only a typed car gets written as a new row, and only when one of
      // theirs was not chosen.
      const car = vehicleId ? "" : val("nj-car");
      if (car) {
        const bits = car.split(/\s+/);
        const year = Number(bits[0]);
        const { data: v } = await sb.from("vehicles").insert({
          account_id: accountId, customer_id: customerId,
          year: year >= 1900 && year <= 2100 ? year : null,
          make: (year ? bits[1] : bits[0]) ?? null,
          model: (year ? bits.slice(2) : bits.slice(1)).join(" ") || null,
        }).select("id").single();
        vehicleId = (v as { id: string } | null)?.id ?? null;
      }

      // A CUSTOMER ON THEIR OWN. Nothing is priced, nothing is booked;
      // somebody is being put on file and that is all.
      if (kind === "customer") {
        shut();
        then(customerId);
        return;
      }

      /**
       * A LEAD IS NOT A JOB. It goes in the leads column with no price on
       * it, which is the point — a lead is somebody who got in touch, and
       * turning that into a quote is a decision an advisor makes.
       */
      if (kind === "lead") {
        const { data: lead, error: leadErr } = await sb.from("enquiries").insert({
          account_id: accountId, customer_id: customerId, vehicle_id: vehicleId,
          kind: "lead", status: "new", source: "counter",
          customer_name: name, customer_phone: val("nj-phone"),
          service_wanted: firstSentence(want).slice(0, 120) || "Got in touch",
          notes: want || null,
        }).select("id").single();
        if (leadErr) throw new Error(`Could not save the lead: ${leadErr.message}`);
        shut();
        then((lead as { id: string }).id);
        return;
      }

      /* Carry forward where this customer's work happens, so a job typed
         at the counter still lands on the day map. See lastKnownLocation.
         Null for somebody new, which leaves it exactly as it was. */
      const knownLocation = await lastKnownLocation(customerId);

      const { data: job, error: jobErr } = await sb.from("jobs").insert({
        account_id: accountId, customer_id: customerId, vehicle_id: vehicleId,
        status: "estimate",
        title: firstSentence(want).slice(0, 90)
          || (kind === "quote" ? "New quote" : "New job"),
        complaint: want || null,
        location: knownLocation,
        created_by: me.user?.id ?? null,
      }).select("id").single();
      if (jobErr) throw new Error(`Could not start it: ${jobErr.message}`);

      shut();
      then((job as { id: string }).id);
    } catch (e) {
      b.disabled = false; b.textContent = w.go;
      say(e instanceof Error ? e.message : String(e));
    }
  });

  (document.getElementById("nj-name") as HTMLInputElement | null)?.focus();
}
